import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getApplicationById } from '../../api/services/ApplicationService';
import { Card, Button, Row, Col, Badge, Spinner, Tab, Tabs, Alert } from 'react-bootstrap';
import ReceiptPDF from '../ui/ReceiptPDF';
import { PDFDownloadLink } from '@react-pdf/renderer';
import {
    FaArrowLeft,
    FaUser,
    FaMapMarkerAlt,
    FaGraduationCap,
    FaFileInvoiceDollar,
    FaCalendarAlt,
    FaIdCard,
    FaExclamationTriangle,
    FaFilePdf
} from 'react-icons/fa';
import styled from 'styled-components';

const SingleApplicationView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState({
        student: null,
        payment: null
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('personal');
    const [application, setApplication] = useState(null);

    useEffect(() => {
        const fetchApplication = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await getApplicationById(id);

                if (!response?.student) {
                    throw new Error('Application not found');
                }

                setData({
                    student: response.student,
                    payment: response.payment || null
                });
                setApplication(response.pdf);
            } catch (err) {
                setError(err.message || 'Failed to load application details');
                console.error('Fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchApplication();
    }, [id]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const options = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const getFullAddress = (address) => {
        if (!address) return 'N/A';
        const { street, city, district, state, postalCode, country } = address;
        return `${street}, ${city}, ${district}, ${state} ${postalCode}, ${country}`;
    };

    const renderStatusBadge = (status) => {
        const statusMap = {
            active: { variant: 'success', label: 'Active' },
            inactive: { variant: 'danger', label: 'Inactive' },
            pending: { variant: 'warning', label: 'Pending' },
            deleted: { variant: 'secondary', label: 'Deleted' }
        };

        const statusInfo = statusMap[status] || { variant: 'secondary', label: status };

        return (
            <StatusBadge bg={statusInfo.variant} className="text-capitalize">
                {statusInfo.label}
            </StatusBadge>
        );
    };

    const renderPaymentStatus = (payment) => {
        if (!payment) {
            return <Badge bg="secondary">No Payment Record</Badge>;
        }

        const statusMap = {
            PAID: { variant: 'success', label: 'Paid' },
            FAILED: { variant: 'danger', label: 'Failed' },
            PENDING: { variant: 'warning', label: 'Pending' },
            REFUNDED: { variant: 'info', label: 'Refunded' },
            CANCELLED: { variant: 'secondary', label: 'Cancelled' }
        };

        const statusInfo = statusMap[payment.orderStatus] || { variant: 'secondary', label: payment.orderStatus };

        return (
            <div className="d-flex align-items-center gap-2">
                <Badge bg={statusInfo.variant} className="text-capitalize">
                    {statusInfo.label}
                </Badge>
                {payment.refundStatus === 'COMPLETED' && (
                    <Badge bg="info">Refunded</Badge>
                )}
            </div>
        );
    };

    const renderPaymentDetails = () => {
        if (!data.payment) {
            return (
                <DetailCard>
                    <Card.Header>
                        <h5><FaFileInvoiceDollar className="me-2" />Payment Information</h5>
                    </Card.Header>
                    <Card.Body>
                        <Alert variant="info" className="mb-0">
                            No payment record found for this application
                        </Alert>
                    </Card.Body>
                </DetailCard>
            );
        }

        return (
            <DetailCard>
                <Card.Header>
                    <h5><FaFileInvoiceDollar className="me-2" />Payment Information</h5>
                </Card.Header>
                <Card.Body>
                    <DetailRow>
                        <LabelCol sm={4}>Order ID:</LabelCol>
                        <ValueCol sm={8}>
                            <code>{data.payment.orderId}</code>
                        </ValueCol>
                    </DetailRow>
                    <DetailRow>
                        <LabelCol sm={4}>Status:</LabelCol>
                        <ValueCol sm={8}>
                            {renderPaymentStatus(data.payment)}
                        </ValueCol>
                    </DetailRow>
                    <DetailRow>
                        <LabelCol sm={4}>Amount:</LabelCol>
                        <ValueCol sm={8}>
                            {data.payment.currency} {data.payment.amount.toFixed(2)}
                        </ValueCol>
                    </DetailRow>
                    <DetailRow>
                        <LabelCol sm={4}>Payment Mode:</LabelCol>
                        <ValueCol sm={8}>{data.payment.paymentMode || 'Online Payment'}</ValueCol>
                    </DetailRow>
                    {data.payment.transactionId && (
                        <DetailRow>
                            <LabelCol sm={4}>Transaction ID:</LabelCol>
                            <ValueCol sm={8}>
                                <code>{data.payment.transactionId}</code>
                            </ValueCol>
                        </DetailRow>
                    )}
                    {data.payment.referenceId && (
                        <DetailRow>
                            <LabelCol sm={4}>Reference ID:</LabelCol>
                            <ValueCol sm={8}>
                                <code>{data.payment.referenceId}</code>
                            </ValueCol>
                        </DetailRow>
                    )}
                    <DetailRow>
                        <LabelCol sm={4}>Payment Date:</LabelCol>
                        <ValueCol sm={8}>{formatDate(data.payment.paymentTime)}</ValueCol>
                    </DetailRow>
                    {data.payment.payerName && (
                        <DetailRow>
                            <LabelCol sm={4}>Payer Name:</LabelCol>
                            <ValueCol sm={8}>{data.payment.payerName}</ValueCol>
                        </DetailRow>
                    )}
                </Card.Body>
            </DetailCard>
        );
    };

    if (loading) {
        return (
            <LoaderContainer>
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-3">Loading application details...</p>
            </LoaderContainer>
        );
    }

    if (error) {
        return (
            <ErrorContainer>
                <Alert variant="danger" className="d-flex align-items-center">
                    <FaExclamationTriangle className="me-2" />
                    <div>
                        <h5>Error Loading Application</h5>
                        <p className="mb-0">{error}</p>
                    </div>
                </Alert>
                <Button
                    variant="outline-primary"
                    onClick={() => navigate(-1)}
                    className="mt-3"
                >
                    <FaArrowLeft className="me-2" />
                    Back to Applications
                </Button>



            </ErrorContainer>
        );
    }

    if (!data.student) {
        return (
            <ErrorContainer>
                <Alert variant="warning" className="d-flex align-items-center">
                    <FaExclamationTriangle className="me-2" />
                    <div>
                        <h5>Application Not Found</h5>
                        <p className="mb-0">The requested application could not be found.</p>
                    </div>
                </Alert>
                <Button
                    variant="outline-primary"
                    onClick={() => navigate(-1)}
                    className="mt-3"
                >
                    <FaArrowLeft className="me-2" />
                    Back to Applications
                </Button>
            </ErrorContainer>
        );
    }

    return (
        <ApplicationViewContainer>
            <HeaderContainer>
                <div className="d-flex justify-content-between align-items-center mb-4 gap-2 flex-wrap">
                    <h2 className="mb-0">Application Details</h2>
                    <div className="ms-auto d-flex gap-2">
                        <BackButton variant="outline-secondary" onClick={() => navigate(-1)}>
                            <FaArrowLeft className="me-2" />
                            Back
                        </BackButton>

                        {application && (
                            <PDFDownloadLink
                                document={<ReceiptPDF application={application} payment={data.payment} />}
                                fileName={`Receipt_${application.orderId}.pdf`}
                                className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                style={{ minWidth: 'fit-content' }}
                            >
                                {({ loading }) => (
                                    <>
                                        <FaFilePdf />
                                        {loading ? 'Preparing...' : 'Download Receipt'}
                                    </>
                                )}
                            </PDFDownloadLink>
                        )}
                    </div>
                </div>


                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
                    <div className="d-flex align-items-center mb-2 mb-md-0 gap-3">
                        <ApplicationNumber>
                            <span className="text-muted">Application No:</span>
                            <strong> {data.student.applicationNo}</strong>
                        </ApplicationNumber>
                        {renderStatusBadge(data.student.status)}
                    </div>
                </div>
            </HeaderContainer>

            <TabsContainer>
                <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                    className="custom-tabs mb-4"
                    id="application-tabs"
                >
                    <Tab eventKey="personal" title={<><FaUser className="me-2" />Personal</>}>
                        <Row>
                            <Col lg={6}>
                                <DetailCard>
                                    <Card.Header>
                                        <h5><FaUser className="me-2" />Personal Information</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <DetailRow>
                                            <LabelCol sm={4}>Full Name:</LabelCol>
                                            <ValueCol sm={8}>{data.student.fullName}</ValueCol>
                                        </DetailRow>
                                        <DetailRow>
                                            <LabelCol sm={4}>Date of Birth:</LabelCol>
                                            <ValueCol sm={8}>
                                                {formatDate(data.student.birthDate)}
                                                {data.student.age && (
                                                    <span className="text-muted ms-2">(Age: {data.student.age})</span>
                                                )}
                                            </ValueCol>
                                        </DetailRow>
                                        <DetailRow>
                                            <LabelCol sm={4}>Gender:</LabelCol>
                                            <ValueCol sm={8}>{data.student.gender}</ValueCol>
                                        </DetailRow>
                                        <DetailRow>
                                            <LabelCol sm={4}>Contact Number:</LabelCol>
                                            <ValueCol sm={8}>{data.student.phone}</ValueCol>
                                        </DetailRow>
                                        <DetailRow>
                                            <LabelCol sm={4}>Email:</LabelCol>
                                            <ValueCol sm={8}>
                                                <a href={`mailto:${data.student.email}`}>
                                                    {data.student.email}
                                                </a>
                                            </ValueCol>
                                        </DetailRow>
                                    </Card.Body>
                                </DetailCard>
                            </Col>

                            <Col lg={6}>
                                <DetailCard>
                                    <Card.Header>
                                        <h5><FaMapMarkerAlt className="me-2" />Address Information</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <DetailRow>
                                            <LabelCol sm={4}>Full Address:</LabelCol>
                                            <ValueCol sm={8}>{getFullAddress(data.student.address)}</ValueCol>
                                        </DetailRow>
                                        <DetailRow>
                                            <LabelCol sm={4}>City:</LabelCol>
                                            <ValueCol sm={8}>{data.student.address.city || 'N/A'}</ValueCol>
                                        </DetailRow>
                                        <DetailRow>
                                            <LabelCol sm={4}>District:</LabelCol>
                                            <ValueCol sm={8}>{data.student.address.district || 'N/A'}</ValueCol>
                                        </DetailRow>
                                        <DetailRow>
                                            <LabelCol sm={4}>State:</LabelCol>
                                            <ValueCol sm={8}>{data.student.address.state}</ValueCol>
                                        </DetailRow>
                                        <DetailRow>
                                            <LabelCol sm={4}>Postal Code:</LabelCol>
                                            <ValueCol sm={8}>{data.student.address.postalCode}</ValueCol>
                                        </DetailRow>
                                        <DetailRow>
                                            <LabelCol sm={4}>Country:</LabelCol>
                                            <ValueCol sm={8}>{data.student.address.country}</ValueCol>
                                        </DetailRow>
                                    </Card.Body>
                                </DetailCard>
                            </Col>
                        </Row>
                    </Tab>

                    <Tab eventKey="education" title={<><FaGraduationCap className="me-2" />Education</>}>
                        <Row>
                            <Col lg={6}>
                                <DetailCard>
                                    <Card.Header>
                                        <h5><FaGraduationCap className="me-2" />Education Details</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <DetailRow>
                                            <LabelCol sm={4}>Qualification:</LabelCol>
                                            <ValueCol sm={8}>
                                                {data.student.educationQualification || 'Not specified'}
                                            </ValueCol>
                                        </DetailRow>
                                    </Card.Body>
                                </DetailCard>
                            </Col>
                        </Row>
                    </Tab>

                    <Tab eventKey="payment" title={<><FaFileInvoiceDollar className="me-2" />Payment</>}>
                        <Row>
                            <Col lg={6}>
                                {renderPaymentDetails()}
                            </Col>
                        </Row>
                    </Tab>

                    <Tab eventKey="metadata" title={<><FaIdCard className="me-2" />Metadata</>}>
                        <Row>
                            <Col lg={6}>
                                <DetailCard>
                                    <Card.Header>
                                        <h5><FaCalendarAlt className="me-2" />Application Timeline</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <DetailRow>
                                            <LabelCol sm={4}>Created At:</LabelCol>
                                            <ValueCol sm={8}>{formatDate(data.student.createdAt)}</ValueCol>
                                        </DetailRow>
                                        <DetailRow>
                                            <LabelCol sm={4}>Last Updated:</LabelCol>
                                            <ValueCol sm={8}>{formatDate(data.student.updatedAt)}</ValueCol>
                                        </DetailRow>
                                    </Card.Body>
                                </DetailCard>
                            </Col>
                            <Col lg={6}>
                                <DetailCard>
                                    <Card.Header>
                                        <h5><FaIdCard className="me-2" />System Information</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <DetailRow>
                                            <LabelCol sm={4}>Application ID:</LabelCol>
                                            <ValueCol sm={8}>
                                                <code>{data.student.id}</code>
                                            </ValueCol>
                                        </DetailRow>
                                        {data.payment && (
                                            <DetailRow>
                                                <LabelCol sm={4}>Payment ID:</LabelCol>
                                                <ValueCol sm={8}>
                                                    <code>{data.payment.id}</code>
                                                </ValueCol>
                                            </DetailRow>
                                        )}
                                    </Card.Body>
                                </DetailCard>
                            </Col>
                        </Row>
                    </Tab>
                </Tabs>
            </TabsContainer>
        </ApplicationViewContainer>
    );
};

// Styled Components
const ApplicationViewContainer = styled.div`
    padding: 2rem;
    max-width: 1400px;
    margin: 0 auto;
`;

const HeaderContainer = styled.header`
    margin-bottom: 2rem;
    
    h1 {
        color: #2c3e50;
        font-weight: 600;
    }
`;

const BackButton = styled(Button)`
    background-color: #fff;
    color: #182848;
    border-color: #182848;
    min-width: 100px;
    
    &:hover {
        background-color: #f8f9fa;
        color: #143e47;
        border-color: #143e47;
    }
`;

const ApplicationNumber = styled.span`
    font-size: 1.1rem;
    color: #495057;
    
    strong {
        color: #2c3e50;
        font-weight: 600;
    }
`;

const TabsContainer = styled.div`
    .custom-tabs {
        .nav-link {
            color: #495057;
            border: none;
            padding: 0.75rem 1.25rem;
            font-weight: 500;
            transition: all 0.2s ease;
            
            &:hover:not(.active) {
                color: #182848;
                background-color: rgba(24, 105, 124, 0.05);
            }
            
            &.active {
                color: #182848;
                border-bottom: 3px solid #182848;
                background-color: transparent;
                font-weight: 600;
            }
        }
    }
`;

const DetailCard = styled(Card)`
    border: none;
    border-radius: 8px;
    box-shadow: 0 2px 15px rgba(0, 0, 0, 0.08);
    margin-bottom: 1.5rem;
    overflow: hidden;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    }
    
    .card-header {
        background: linear-gradient(135deg, #4b6cb7 0%, #182848 100%);
        color: white;
        padding: 1rem 1.5rem;
        border-bottom: none;
        
        h5 {
            margin: 0;
            font-size: 1.1rem;
            display: flex;
            align-items: center;
            font-weight: 500;
        }
    }
    
    .card-body {
        padding: 1.5rem;
    }
`;

const DetailRow = styled(Row)`
    margin-bottom: 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #f0f0f0;
    
    &:last-child {
        border-bottom: none;
        margin-bottom: 0;
        padding-bottom: 0;
    }
`;

const LabelCol = styled(Col)`
    font-weight: 600;
    color: #495057;
`;

const ValueCol = styled(Col)`
    color: #212529;
    word-break: break-word;
    
    code {
        background-color: #f5f5f5;
        padding: 2px 4px;
        border-radius: 3px;
        color: #d63384;
        font-size: 0.9em;
    }
`;

const StatusBadge = styled(Badge)`
    font-size: 0.875rem;
    padding: 0.5rem 0.75rem;
    font-weight: 500;
    letter-spacing: 0.5px;
`;

const LoaderContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 70vh;
    
    p {
        color: #6c757d;
        margin-top: 1rem;
    }
`;

const ErrorContainer = styled.div`
    max-width: 800px;
    margin: 2rem auto;
    padding: 2rem;
    text-align: center;
    
    .alert {
        text-align: left;
        padding: 1.5rem;
        border-radius: 8px;
        
        h5 {
            font-weight: 600;
            margin-bottom: 0.5rem;
        }
    }
`;

export default SingleApplicationView;